import React, { useEffect, useMemo, useState } from 'react';
import { TextField, InputAdornment, Select, MenuItem, Box } from '@mui/material';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';

const PhoneNumberField = ({
  value,
  onChange,
  label = 'Téléphone',
  name = 'phoneNumber',
  id = 'phoneNumber',
  required = false,
  error = false,
  helperText = '',
  defaultCountry = 'GN',
  maxDigits = 15,
  ...rest
}) => {
  const countries = useMemo(() => getCountries(), []);
  const [country, setCountry] = useState(defaultCountry);
  const [displayValue, setDisplayValue] = useState(value || '');
  const getFlagEmoji = (code) => {
    if (!code || code.length !== 2) return '';
    const A = 0x1f1e6;
    const base = 'A'.charCodeAt(0);
    return String.fromCodePoint(
      A + code.charCodeAt(0) - base,
      A + code.charCodeAt(1) - base
    );
  };

  useEffect(() => {
    if (!value) {
      setDisplayValue('');
      return;
    }
    const parsed = parsePhoneNumberFromString(value);
    if (parsed?.country) {
      setCountry(parsed.country);
    }
    const formatter = new AsYouType(parsed?.country || country);
    setDisplayValue(formatter.input(value));
  }, [value, country]);

  const handleInputChange = (e) => {
    const nextRaw = e.target.value;
    const digits = String(nextRaw).replace(/\D/g, '');
    if (digits.length > maxDigits) return;

    const formatter = new AsYouType(country);
    const formatted = formatter.input(nextRaw);
    setDisplayValue(formatted);

    const e164 = formatter.getNumberValue() || '';
    onChange(e164);
  };

  return (
    <TextField
      fullWidth
      label={label}
      name={name}
      id={id}
      value={displayValue}
      onChange={handleInputChange}
      required={required}
      error={error}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 1 }}>
            <Select
              value={country}
              onChange={(e) => {
                const nextCountry = e.target.value;
                setCountry(nextCountry);
                const formatter = new AsYouType(nextCountry);
                const formatted = formatter.input(displayValue);
                setDisplayValue(formatted);
                const e164 = formatter.getNumberValue() || '';
                onChange(e164);
              }}
              variant="standard"
              disableUnderline
              sx={{ minWidth: 72 }}
              renderValue={(code) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{getFlagEmoji(code)}</span>
                  <span>{`+${getCountryCallingCode(code)}`}</span>
                </Box>
              )}
            >
              {countries.map((code) => (
                <MenuItem key={code} value={code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{getFlagEmoji(code)}</span>
                    <span>{code}</span>
                    <span>+{getCountryCallingCode(code)}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        ),
        inputProps: { maxLength: maxDigits + 4 },
      }}
      {...rest}
    />
  );
};

export default PhoneNumberField;
